import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link } from "@tanstack/react-router";
import { useRegister } from "../hooks/use-register";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { AlertCircle, Loader2 } from "lucide-react";

const inputStyles =
  "rounded-lg border-neutral-200 focus:border-neutral-500 focus:ring-neutral-500";

export function RegisterForm() {
  const { form, onSubmit, isPending, error } = useRegister();

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4 w-full"
        noValidate
        aria-label="Registration form"
      >
        <FormField
          control={form.control}
          name="firstName"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  placeholder="First name"
                  type="text"
                  autoComplete="name"
                  className={inputStyles}
                  aria-required="true"
                  aria-label="First name"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  placeholder="Last name"
                  type="text"
                  autoComplete="name"
                  className="rounded-lg border-neutral-200 focus:border-neutral-500 focus:ring-neutral-500"
                  aria-required="true"
                  aria-label="Last name"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  placeholder="name@example.com"
                  type="email"
                  autoComplete="email"
                  className={inputStyles}
                  aria-required="true"
                  aria-label="Email address"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="instituteName"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  placeholder="Institution name"
                  type="text"
                  autoComplete="institution-name"
                  className={inputStyles}
                  aria-required="true"
                  aria-label="Institution name"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid gap-4 sm:grid-cols-1">
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    placeholder="Create a password"
                    type="password"
                    autoComplete="new-password"
                    className={inputStyles}
                    aria-required="true"
                    aria-label="Password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    placeholder="Confirm your password"
                    type="password"
                    autoComplete="new-password"
                    className={inputStyles}
                    aria-required="true"
                    aria-label="Confirm password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Button
          className="w-full bg-neutral-950 text-white transition-all hover:bg-neutral-800"
          type="submit"
          aria-busy={isPending}
          disabled={isPending}
        >
          {isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
          )}
          Create Account
        </Button>

        {error && (
          <div
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-red-50 p-2 text-red-500"
            role="alert"
            aria-live="polite"
          >
            <AlertCircle className="size-4" aria-hidden="true" />
            <p className="text-sm font-medium">{error}</p>
          </div>
        )}

        <div className="flex items-center justify-center gap-2 text-sm ">
          <p className="text-sm font-medium text-neutral-600">
            Already have an account?
          </p>
          <Link
            to="/login"
            className="font-medium text-primary transition-colors"
          >
            Sign in
          </Link>
        </div>
      </form>
    </Form>
  );
}
